import { ConversationService } from './../../shared/services/conversation.service';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as fromConversationActions from './actions';
import * as fromMessageActions from '../message/actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import { Update } from '@ngrx/entity';
import { Conversation } from '@repo/shared/src/services/messaging-service/messaging-service.types';
import * as fromConversationSelectors from './selectors';
import * as fromMessageSelectors from '../message/selectors';
import { CreateConversationRequest } from '@repo/shared/src/back/back-messages.types';
import { NewConversationService } from '../../shared/services/new-conversation.service';

@Injectable()
export class ConversationEffects {
  getConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromConversationActions.getConversationList, fromConversationActions.cancelGetConversationList),
      switchMap(action => {
        if (action.type === '[Conversation] Cancel Get Conversation List') {
          return of(fromConversationActions.cancelGetConversationListSuccess());
        } else {
          return this.conversationService.getConversationList().pipe(
            map(conversationResponse => {
              const conversations$ = this.store.select(fromConversationSelectors.getConversationList);

              let isSameResult = false;

              conversations$
                .subscribe(conversations => {
                  if (
                    conversations[0]?.id === conversationResponse.data.conversations[0].id &&
                    conversations[0]?.lastUpdateDate === conversationResponse.data.conversations[0].lastUpdateDate &&
                    conversations[0]?.lastMessageReceivedContent ===
                      conversationResponse.data.conversations[0].lastMessageReceivedContent
                  ) {
                    isSameResult = true;
                  }
                })
                .unsubscribe();
              if (isSameResult) {
                return fromConversationActions.cancelGetConversationListSuccess();
              } else {
                return fromConversationActions.getConversationListSuccess({
                  conversationList: conversationResponse.data.conversations,
                });
              }
            }),
            catchError(error => {
              return of(
                fromConversationActions.getConversationListFailed({
                  error: error.message,
                  isLoadingNeeded: action.isLoadingNeeded,
                }),
              );
            }),
          );
        }
      }),
    ),
  );

  postConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromConversationActions.postConversation),
      mergeMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());

        const createConversation: CreateConversationRequest = {
          subject: action.conversation.subject,
          participants: action.conversation.participants,
          status: action.conversation.status,
        };

        return this.conversationService.postConversation(createConversation).pipe(
          map(conversationResponse =>
            fromConversationActions.postConversationSuccess({
              conversation: conversationResponse.data,
            }),
          ),
          catchError(error => of(fromConversationActions.postConversationFailed({ error: error.message }))),
        );
      }),
    ),
  );

  postConversationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromConversationActions.postConversationSuccess),
      mergeMap(action => {
        return of(
          fromConversationActions.setConversationSelected({
            conversationSelected: action.conversation,
          }),
        );
      }),
    ),
  );

  updateConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromConversationActions.updateConversation),
      mergeMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return this.conversationService.patchConversation(action.changes, action.conversationId).pipe(
          map(() => {
            const conversationUpdated: Update<Conversation> = {
              id: action.conversationId,
              changes: action.changes,
            };

            if (action.conversation) {
              this.store.dispatch(
                fromConversationActions.setConversationSelected({
                  conversationSelected: action.conversation,
                }),
              );
            }

            return fromConversationActions.updateConversationSuccess({
              conversationUpdated,
            });
          }),
          catchError(error => of(fromConversationActions.postConversationFailed({ error: error.message }))),
        );
      }),
    ),
  );

  deleteConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromConversationActions.deleteConversation),
      mergeMap(action => {
        this.store.dispatch(fromConversationActions.cancelGetConversationList());
        this.store
          .select(fromMessageSelectors.idMessagesLoaded)
          .subscribe(id => {
            if (id) {
              this.store.dispatch(fromMessageActions.cancelGetMessageList());
            }
          })
          .unsubscribe();

        return this.conversationService.deleteConversationById(action.conversationId).pipe(
          map(() =>
            fromConversationActions.deleteConversationSuccess({
              conversationId: action.conversationId,
            }),
          ),
          catchError(error => of(fromConversationActions.deleteConversationFailed({ error: error.message }))),
        );
      }),
    ),
  );

  isRedirectedFromAppointment$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromConversationActions.initiateConversation),
        tap(({ redirectedFromAppointment: redirectedFromAppointment }) => {
          return this.newConversationService.setRedirectedFromAppointment(redirectedFromAppointment);
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private conversationService: ConversationService,
    private newConversationService: NewConversationService,
    private readonly store: Store<AppState>,
  ) {}
}
